import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type HeaderProps = {
  language: Language;
  onLanguageChange: (lang: Language) => void;
};
const navItems = {
  ua: ['Продукція', 'Каталоги', 'Відео', 'Партнери', 'Новини', 'Завантажити', 'Контакти'],
  en: ['Products', 'Catalogs', 'Video', 'Partners', 'News', 'Downloads', 'Contact']
};
const ctaText = {
  ua: {
    partner: 'Стати партнером',
    cabinet: 'Кабінет'
  },
  en: {
    partner: 'Become a Partner',
    cabinet: 'Cabinet'
  }
};
export function Header({
  language,
  onLanguageChange
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <motion.header initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-chamaleon-black/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`} data-id="element-115">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-116">
        <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-117">
          {/* Logo */}
          <motion.a href="#" className="flex items-center gap-2 group" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-118">
            <div className="relative" data-id="element-119">
              <div className="w-10 h-10 bg-gradient-to-br from-chamaleon-orange to-chamaleon-orange-dark rounded-lg flex items-center justify-center" data-id="element-120">
                <span className="text-white font-bold text-xl" data-id="element-121">C</span>
              </div>
              <div className="absolute inset-0 bg-chamaleon-orange/30 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-122" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight hidden sm:block" data-id="element-123">
              CHAMALEON
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-id="element-124">
            {navItems[language].map((item, index) => <motion.a key={item} href="#" className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors relative group" initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05 + 0.3
          }} data-id="element-125">
                {item}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-chamaleon-orange group-hover:w-full transition-all duration-300" data-id="element-126" />
              </motion.a>)}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3" data-id="element-127">
            {/* Language Switcher */}
            <div className="hidden sm:flex items-center gap-1 bg-chamaleon-charcoal/50 rounded-full p-1" data-id="element-128">
              <button onClick={() => onLanguageChange('ua')} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-300 ${language === 'ua' ? 'bg-chamaleon-orange text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-129">
                UA
              </button>
              <button onClick={() => onLanguageChange('en')} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-300 ${language === 'en' ? 'bg-chamaleon-orange text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-130">
                EN
              </button>
            </div>

            {/* Cabinet Button - Desktop */}
            <a href="#" className="hidden md:block px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors" data-id="element-131">
              {ctaText[language].cabinet}
            </a>

            {/* Partner CTA */}
            <motion.a href="#partner" className="relative px-5 py-2.5 bg-chamaleon-orange text-white text-sm font-semibold rounded-lg overflow-hidden group" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-132">
              <span className="relative z-10" data-id="element-133">{ctaText[language].partner}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-chamaleon-orange-dark to-chamaleon-orange opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-134" />
              <div className="absolute inset-0 bg-chamaleon-orange/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-135" />
            </motion.a>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-white" aria-label="Toggle menu" data-id="element-136">
              {isMobileMenuOpen ? <XIcon size={24} data-id="element-137" /> : <MenuIcon size={24} data-id="element-138" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-139">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="lg:hidden bg-chamaleon-black/95 backdrop-blur-xl border-t border-white/5" data-id="element-140">
            <nav className="max-w-7xl mx-auto px-4 py-6 space-y-1" data-id="element-141">
              {navItems[language].map((item, index) => <motion.a key={item} href="#" className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-chamaleon-charcoal/50 rounded-lg transition-colors" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} onClick={() => setIsMobileMenuOpen(false)} data-id="element-142">
                  {item}
                </motion.a>)}

              {/* Mobile Language Switcher */}
              <div className="flex items-center gap-2 px-4 py-3" data-id="element-143">
                <span className="text-gray-500 text-sm" data-id="element-144">Мова:</span>
                <button onClick={() => onLanguageChange('ua')} className={`px-3 py-1.5 text-sm rounded-lg ${language === 'ua' ? 'bg-chamaleon-orange text-white' : 'text-gray-400'}`} data-id="element-145">
                  UA
                </button>
                <button onClick={() => onLanguageChange('en')} className={`px-3 py-1.5 text-sm rounded-lg ${language === 'en' ? 'bg-chamaleon-orange text-white' : 'text-gray-400'}`} data-id="element-146">
                  EN
                </button>
              </div>

              {/* Mobile Cabinet Link */}
              <a href="#" className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-chamaleon-charcoal/50 rounded-lg transition-colors" data-id="element-147">
                {ctaText[language].cabinet}
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}